/*
  Content pages (stage א6):
  - <amor-form>: the contact, return and cancellation forms (spec chapter 35)
    and Find My Piece (chapter 32). It checks every field before sending,
    puts the focus on the first one missing, opens the rows that depend on a
    choice (data-show-when), fills her details when she is signed in and the
    order or the text from the address (?order=, ?q=). The three forms are
    Shopify's contact form, sent without leaving the page; Find My Piece is
    saved in the demo layer only. Sent once even on a double click; on
    success the form is replaced by a message and a summary of what was
    sent; on failure the fields stay filled.
  - <amor-balance>: the Gift Card balance check (chapter 25), mock.
  - <amor-boutique-status> and the hours lists: "open now" from the
    boutique's hours (chapter 30), and today's row marked.
  - Secret Room (chapter 24): "available in your size" from her sizes.
  Storage goes only through window.DemoStore.
*/
(() => {
  const store = () => window.DemoStore;
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const MOCK_SAVE_MS = 700;

  const checks = {
    required: (value) => value.trim() !== '',
    name: (value) => value.trim().length >= 2,
    phone: (value) =>
      window.AmorAccount?.validPhone ? window.AmorAccount.validPhone(value) : /^0\d{8,9}$/.test(value.replace(/\D/g, '')),
    email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim()),
    order: (value) => /^(AM-?)?\d{4,6}$/i.test(value.replace(/\s/g, '')),
    sku: (value) => /^\d{9}$/.test(value.replace(/\s/g, '')),
    url: (value) => /^(https?:\/\/)?[^\s/.]+\.[^\s]{2,}$/i.test(value.trim()),
  };

  /** AM-10482 from "am 10482", "10482" or "AM10482". */
  const normalizeOrder = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits ? `AM-${digits}` : '';
  };

  /* -------------------------------------------------------------------------
     <amor-form>
  ------------------------------------------------------------------------- */
  class AmorForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.done = this.querySelector('[data-form-done]');
      this.failed = this.querySelector('[data-form-failed]');
      this.send = this.querySelector('[data-form-send]');
      this.sendLabel = this.querySelector('[data-send-label]');
      this.sendText = this.sendLabel?.textContent;
      this.form.noValidate = true;

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('input', (event) => this.mark(event.target.closest('.form-row'), false));

      this.prefill();
      this.applyConditions();
      // Signing in from the page fills her details and shows her order's items at once.
      document.addEventListener('demo-store:change', (event) => {
        if (event.detail.key !== 'signedIn') return;
        this.prefill();
        this.applyConditions();
      });
    }

    /** Her details when signed in; the order and the text from the address. */
    prefill() {
      const params = new URLSearchParams(window.location.search);
      const signedIn = store()?.signedIn() === true;
      const details = signedIn ? store().details() : {};
      const order = normalizeOrder(params.get('order'));
      const values = { order, q: params.get('q') || '', name: details.name, phone: details.phone, email: details.email };
      this.querySelectorAll('[data-prefill]').forEach((input) => {
        const value = values[input.dataset.prefill];
        if (value && !input.value) input.value = value;
      });

      // Coming from one of Noa's orders: pick the item instead of typing a SKU.
      this.picker = null;
      this.querySelectorAll('[data-order-items]').forEach((set) => {
        const show = signedIn && set.dataset.orderItems === order;
        set.hidden = !show;
        set.disabled = !show;
        if (show) this.picker = set;
      });
      const skuRow = this.querySelector('[data-sku-row]');
      if (skuRow) {
        skuRow.hidden = Boolean(this.picker);
        skuRow.querySelectorAll('input').forEach((input) => (input.disabled = Boolean(this.picker)));
      }
    }

    /** Rows shown only for a choice ("type=החלפה"); a hidden row is not sent and not checked. */
    applyConditions() {
      this.querySelectorAll('[data-show-when]').forEach((row) => {
        const [field, value] = row.dataset.showWhen.split('=');
        const chosen = this.form.querySelector(`input[data-field="${field}"]:checked`);
        const show = chosen?.value === value;
        row.hidden = !show;
        if (row.matches('fieldset')) row.disabled = !show;
        else row.querySelectorAll('input, textarea, select').forEach((input) => (input.disabled = !show));
        if (!show) this.mark(row, false);
      });
    }

    onChange(event) {
      const row = event.target.closest('.form-row');
      this.mark(row, false);
      if (event.target.matches('[data-field]')) this.applyConditions();
      // Picking her item fills the requested SKU of an exchange with the same item.
      if (event.target.closest('[data-order-items]')) {
        this.querySelectorAll('[data-prefill="pick"]').forEach((input) => {
          if (!input.value || input.dataset.picked === input.value) {
            input.value = event.target.value;
            input.dataset.picked = event.target.value;
          }
        });
      }
    }

    /** Checks every shown row in page order; returns the first one missing, or null. */
    validate() {
      let first = null;
      this.form.querySelectorAll('.form-row').forEach((row) => {
        if (row.closest('[hidden]') || row.disabled) return;
        let ok = true;
        let target = null;
        if (row.hasAttribute('data-check-group')) {
          ok = Boolean(row.querySelector('input:checked'));
          target = row.querySelector('input');
        } else {
          const input = row.querySelector('[data-check]');
          if (!input || input.disabled) return;
          const value = input.value || '';
          const check = checks[input.dataset.check] || checks.required;
          ok = (input.hasAttribute('data-optional') && value.trim() === '') || check(value);
          target = input;
        }
        this.mark(row, !ok);
        if (!ok && !first) first = target;
      });
      return first;
    }

    mark(row, invalid) {
      if (!row) return;
      row.classList.toggle('is-invalid', invalid);
      const message = row.querySelector(':scope > .field-message--error');
      if (message) message.hidden = !invalid;
      const input = row.querySelector('[data-check]');
      if (input) input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    }

    /** What was filled in, in page order: [{ label, value }]. */
    summary() {
      const rows = [];
      this.form.querySelectorAll('.form-row').forEach((row) => {
        if (row.closest('[hidden]') || row.disabled) return;
        const label = row.querySelector('[data-row-label]')?.textContent.trim();
        let value = '';
        const checked = row.querySelector('input:checked');
        if (checked) {
          value = checked.dataset.summary
            ? `${checked.dataset.summary} · ${checked.value}`
            : checked.closest('label')?.textContent.trim() || checked.value;
        } else {
          const input = row.querySelector('input:not([type="radio"]), textarea');
          if (!input || input.disabled) return;
          value = input.type === 'file' ? [...input.files].map((file) => file.name).join(', ') : input.value.trim();
        }
        if (label && value) rows.push({ label, value });
      });
      return rows;
    }

    setSending(sending) {
      this.send.disabled = sending;
      this.send.classList.toggle('is-loading', sending);
      this.send.setAttribute('aria-busy', sending ? 'true' : 'false');
      if (this.sendLabel) this.sendLabel.textContent = sending ? this.dataset.sending : this.sendText;
    }

    async onSubmit(event) {
      // Shopify binds its own listener to contact forms that submits the page
      // natively; ours is bound first, so stopping here keeps the visitor on the page.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      this.failed.hidden = true;
      const first = this.validate();
      if (first) {
        first.focus();
        return;
      }

      this.busy = true;
      this.setSending(true);
      const rows = this.summary();
      let ok = false;
      try {
        if (this.dataset.mode === 'mock') {
          await wait(MOCK_SAVE_MS);
          ok = true;
        } else {
          // Shopify answers a sent contact form by going back to the page with ?contact_posted=true.
          const response = await fetch(this.form.action, { method: 'POST', body: new FormData(this.form) });
          ok = response.ok && /[?&]contact_posted=true/.test(response.url);
        }
      } catch {
        ok = false;
      }
      this.busy = false;
      this.setSending(false);

      if (!ok) {
        this.failed.hidden = false;
        return;
      }
      this.record(rows);
      this.showDone(rows);
    }

    /** The demo layer's part: the order's flag in the account area, or the saved request. */
    record(rows) {
      const kind = this.dataset.request;
      if (kind === 'return' || kind === 'cancel') {
        const order = normalizeOrder(this.form.querySelector('[data-prefill="order"]')?.value);
        if (order) store()?.addFormRequest({ type: kind, order });
      } else if (kind === 'find') {
        store()?.addFindRequest({ rows });
      }
    }

    showDone(rows) {
      const list = this.done.querySelector('[data-form-summary]');
      list.replaceChildren(
        ...rows.map(({ label, value }) => {
          const row = document.createElement('div');
          row.className = 'form-done__row';
          const term = document.createElement('dt');
          term.textContent = label;
          const detail = document.createElement('dd');
          detail.textContent = value;
          row.append(term, detail);
          return row;
        })
      );
      this.form.hidden = true;
      this.done.hidden = false;
      this.done.focus({ preventScroll: true });
      this.done.scrollIntoView({ block: 'start', behavior: window.AmorMotion?.scroll() || 'smooth' });
    }
  }
  customElements.define('amor-form', AmorForm);

  /* -------------------------------------------------------------------------
     <amor-balance>: 16 digits in four groups; the demo card shows its
     balance, another number is not found, fewer digits are not a number.
  ------------------------------------------------------------------------- */
  class AmorBalance extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-balance-input]');
      this.row = this.input.closest('.form-row');
      this.error = this.querySelector('[data-balance-error]');
      this.result = this.querySelector('[data-balance-result]');
      this.card = (this.dataset.card || '').replace(/\D/g, '');

      const from = new URLSearchParams(window.location.search).get('card');
      if (from) this.input.value = this.format(from);

      this.input.addEventListener('input', () => {
        const atEnd = this.input.selectionStart === this.input.value.length;
        if (atEnd) this.input.value = this.format(this.input.value);
        this.show(null);
      });
      this.querySelector('form').addEventListener('submit', (event) => {
        event.preventDefault();
        this.check();
      });

      const expiry = this.querySelector('[data-balance-expiry]');
      if (expiry && store()) {
        const date = store().dateFromDays(0);
        date.setFullYear(date.getFullYear() + Number(expiry.dataset.years || 0));
        expiry.dateTime = store().isoDate(date);
        expiry.textContent = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
      }
    }

    format(value) {
      return String(value)
        .replace(/\D/g, '')
        .slice(0, 16)
        .replace(/(\d{4})(?=\d)/g, '$1 ');
    }

    /** null clears; 'short' | 'none' | 'found'. */
    show(state) {
      const invalid = state === 'short' || state === 'none';
      this.row.classList.toggle('is-invalid', invalid);
      this.input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      this.error.hidden = !invalid;
      if (invalid) this.error.textContent = state === 'short' ? this.dataset.short : this.dataset.none;
      this.result.hidden = state !== 'found';
    }

    check() {
      const digits = this.input.value.replace(/\D/g, '');
      if (digits.length < 16) {
        this.show('short');
        this.input.focus();
      } else if (digits !== this.card) {
        this.show('none');
        this.input.focus();
      } else {
        this.show('found');
        this.result.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('amor-balance', AmorBalance);

  /* -------------------------------------------------------------------------
     The boutique's hours: "10:00–20:00" per weekday from Sunday, empty when
     closed (theme settings). "Open now" in Deep Green, or "closed, opens
     tomorrow at 10:00" in Smoke (spec chapter 30).
  ------------------------------------------------------------------------- */
  const parseHours = (text) => {
    const match = String(text || '').match(/(\d{1,2}):(\d{2})\s*[–—-]\s*(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return { open: Number(match[1]) * 60 + Number(match[2]), close: Number(match[3]) * 60 + Number(match[4]), from: `${match[1].padStart(2, '0')}:${match[2]}` };
  };

  class AmorBoutiqueStatus extends HTMLElement {
    connectedCallback() {
      const hours = this.dataset.hours.split('|').map(parseHours);
      const days = this.dataset.days.split(',');
      const now = new Date();
      const today = now.getDay();
      const minutes = now.getHours() * 60 + now.getMinutes();
      const todays = hours[today];
      let text;
      let open = false;
      if (todays && minutes >= todays.open && minutes < todays.close) {
        open = true;
        text = this.dataset.open;
      } else if (todays && minutes < todays.open) {
        text = this.dataset.today.replace('__TIME__', todays.from);
      } else {
        for (let ahead = 1; ahead <= 7; ahead += 1) {
          const day = (today + ahead) % 7;
          if (!hours[day]) continue;
          text = (ahead === 1 ? this.dataset.tomorrow : this.dataset.day.replace('__DAY__', days[day])).replace(
            '__TIME__',
            hours[day].from
          );
          break;
        }
      }
      this.classList.toggle('is-open', open);
      this.textContent = text || '';
      this.hidden = !text;
    }
  }
  customElements.define('amor-boutique-status', AmorBoutiqueStatus);

  const markToday = () => {
    const today = String(new Date().getDay());
    document.querySelectorAll('[data-boutique-hours] [data-day]').forEach((row) => {
      row.classList.toggle('is-today', row.dataset.day === today);
    });
  };
  /* -------------------------------------------------------------------------
     Secret Room (spec chapter 24): "available in your size, IT 40", from her
     editable sizes, only when that size is in stock.
  ------------------------------------------------------------------------- */
  const fillYourSize = () => {
    document.querySelectorAll('[data-your-size]').forEach((line) => {
      const size = store()?.signedIn() ? store().sizeFor(line.dataset.yourSize) : null;
      const available = line.dataset.sizes.split('|').filter(Boolean);
      const show = Boolean(size && available.includes(size));
      line.hidden = !show;
      if (show) line.textContent = line.dataset.text.replace('__SIZE__', size);
    });
  };
  document.addEventListener('demo-store:change', (event) => {
    if (['signedIn', 'profile'].includes(event.detail.key)) fillYourSize();
  });

  const onReady = () => {
    markToday();
    fillYourSize();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})();
